import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import {
  LIBELLES_MOYEN_DEPENSE,
  MOYENS_PAIEMENT_DEPENSE,
  depenseInputSchema,
  type CategorieDepense,
  type Depense,
  type MoyenPaiementDepense,
} from '@iobiz/shared'
import { ApiError, api } from '../api/client.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { ChampDate } from '../ui/ChampDate.js'

/**
 * Les DÉPENSES COURANTES d'une boutique.
 *
 * ⭐ Ce qu'on paie pour tourner et qui n'entre pas dans le stock : loyer,
 * électricité, transport, petit matériel. Deux onglets, parce que ce sont deux
 * gestes différents — on saisit une dépense tous les jours, on crée une
 * catégorie trois fois par an.
 */
const VIDE = {
  label: '',
  amount: '',
  categoryId: '',
  spentOn: '',
  paymentMethod: 'cash' as MoyenPaiementDepense,
  supplierName: '',
  notes: '',
}

function aujourdhui(): string {
  // ⚠️ Composantes LOCALES : `toISOString()` recule d'un jour en soirée à l'est
  // de Greenwich, et la dépense du soir tomberait la veille.
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function DepensesPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const storeId = store?.storeId
  const monnaie = store?.currencyCode ?? 'XAF'

  const [onglet, setOnglet] = useState(0)
  const [depenses, setDepenses] = useState<Depense[]>([])
  const [total, setTotal] = useState(0)
  const [categories, setCategories] = useState<CategorieDepense[]>([])
  const [erreur, setErreur] = useState<string | null>(null)

  const [saisie, setSaisie] = useState<(typeof VIDE & { id?: string }) | null>(null)
  const [categorie, setCategorie] = useState<{ id?: string; name: string } | null>(null)

  const charger = useCallback(async (): Promise<void> => {
    if (!storeId) return
    try {
      const [d, c] = await Promise.all([
        api.get<{ data: Depense[]; totalMontant: number }>(`/stores/${storeId}/expenses?limit=200`),
        api.get<{ data: CategorieDepense[] }>(`/stores/${storeId}/expense-categories`),
      ])
      setDepenses(d.data)
      setTotal(d.totalMontant)
      setCategories(c.data)
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.')
    }
  }, [storeId])

  useEffect(() => {
    void charger()
  }, [charger])

  const format = (n: number): string => `${new Intl.NumberFormat('fr-FR').format(n)} ${monnaie}`

  async function enregistrer(): Promise<void> {
    if (!storeId || !saisie) return
    setErreur(null)
    const parsed = depenseInputSchema.safeParse({
      label: saisie.label,
      amount: saisie.amount,
      categoryId: saisie.categoryId || undefined,
      spentOn: saisie.spentOn || aujourdhui(),
      paymentMethod: saisie.paymentMethod,
      supplierName: saisie.supplierName || undefined,
      notes: saisie.notes || undefined,
    })
    if (!parsed.success) {
      setErreur(parsed.error.issues[0]?.message ?? 'Saisie invalide')
      return
    }
    try {
      if (saisie.id) await api.patch(`/stores/${storeId}/expenses/${saisie.id}`, parsed.data)
      else await api.post(`/stores/${storeId}/expenses`, parsed.data)
      setSaisie(null)
      await charger()
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Enregistrement impossible.')
    }
  }

  async function enregistrerCategorie(): Promise<void> {
    if (!storeId || !categorie) return
    setErreur(null)
    try {
      const corps = { name: categorie.name, position: 0 }
      if (categorie.id) {
        await api.patch(`/stores/${storeId}/expense-categories/${categorie.id}`, corps)
      } else {
        await api.post(`/stores/${storeId}/expense-categories`, corps)
      }
      setCategorie(null)
      await charger()
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Enregistrement impossible.')
    }
  }

  async function supprimer(chemin: string): Promise<void> {
    if (!storeId) return
    setErreur(null)
    try {
      await api.delete(`/stores/${storeId}/${chemin}`)
      await charger()
    } catch (e) {
      // Le serveur refuse de supprimer une catégorie qui classe des dépenses :
      // son message dit quoi faire, on le relaie tel quel.
      setErreur(e instanceof ApiError ? e.message : 'Suppression impossible.')
    }
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 1 }}>
        Dépenses courantes
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Ce que la boutique paie pour tourner — hors achats de marchandise.
      </Typography>

      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      <Tabs value={onglet} onChange={(_, v: number) => setOnglet(v)} sx={{ mb: 4 }}>
        <Tab label={`Dépenses (${depenses.length})`} />
        <Tab label={`Catégories (${categories.length})`} />
      </Tabs>

      {onglet === 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Chip label={`Total : ${format(total)}`} color="primary" />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setSaisie({ ...VIDE, spentOn: aujourdhui() })}
            >
              Nouvelle dépense
            </Button>
          </Box>

          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {depenses.length === 0 ? (
              <Box sx={{ p: 10, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ mb: 2 }}>
                  Aucune dépense
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Enregistrez le loyer, l’électricité, le transport — tout ce qui sort de la caisse
                  sans entrer en stock.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Libellé</TableCell>
                    <TableCell>Catégorie</TableCell>
                    <TableCell>Règlement</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {depenses.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(`${d.spentOn}T12:00:00`).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {d.label}
                        {d.supplierName && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                            {d.supplierName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.categoryName ? (
                          <Chip size="small" variant="outlined" label={d.categoryName} />
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            non classée
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {LIBELLES_MOYEN_DEPENSE[d.paymentMethod]}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {format(d.amount)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Modifier ${d.label}`}
                          onClick={() =>
                            setSaisie({
                              id: d.id,
                              label: d.label,
                              amount: String(d.amount),
                              categoryId: d.categoryId ?? '',
                              spentOn: d.spentOn,
                              paymentMethod: d.paymentMethod,
                              supplierName: d.supplierName ?? '',
                              notes: d.notes ?? '',
                            })
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Supprimer ${d.label}`}
                          onClick={() => void supprimer(`expenses/${d.id}`)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      {onglet === 1 && (
        <>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCategorie({ name: '' })}
            >
              Nouvelle catégorie
            </Button>
          </Box>

          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {categories.length === 0 ? (
              <Box sx={{ p: 10, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Aucune catégorie. Créez celles de votre métier — loyer, énergie, transport… Une
                  liste imposée finirait en « Divers » pour tout.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Catégorie</TableCell>
                    <TableCell align="right">Dépenses</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{c.name}</TableCell>
                      <TableCell align="right">{c.expenseCount}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {format(c.total)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Modifier ${c.name}`}
                          onClick={() => setCategorie({ id: c.id, name: c.name })}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={`Supprimer ${c.name}`}
                          onClick={() => void supprimer(`expense-categories/${c.id}`)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      {/* ─────────────────────────────────────────────── saisie d'une dépense */}
      <Dialog open={saisie !== null} onClose={() => setSaisie(null)} fullWidth maxWidth="sm">
        <DialogTitle>{saisie?.id ? 'Modifier la dépense' : 'Nouvelle dépense'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
            <TextField
              label="Libellé"
              value={saisie?.label ?? ''}
              autoFocus
              onChange={(e) => setSaisie((s) => s && { ...s, label: e.target.value })}
            />
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                label={`Montant (${monnaie})`}
                type="number"
                value={saisie?.amount ?? ''}
                onChange={(e) => setSaisie((s) => s && { ...s, amount: e.target.value })}
              />
              {/* Le calendrier de l'application, comme partout ailleurs. */}
              <ChampDate
                label="Date de la dépense"
                value={saisie?.spentOn ?? ''}
                onChange={(v) => setSaisie((s) => s && { ...s, spentOn: v })}
              />
            </Box>
            <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                select
                label="Catégorie"
                value={saisie?.categoryId ?? ''}
                onChange={(e) => setSaisie((s) => s && { ...s, categoryId: e.target.value })}
              >
                <MenuItem value="">Non classée</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Règlement"
                value={saisie?.paymentMethod ?? 'cash'}
                onChange={(e) =>
                  setSaisie(
                    (s) => s && { ...s, paymentMethod: e.target.value as MoyenPaiementDepense },
                  )
                }
              >
                {MOYENS_PAIEMENT_DEPENSE.map((m) => (
                  <MenuItem key={m} value={m}>
                    {LIBELLES_MOYEN_DEPENSE[m]}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              label="Bénéficiaire"
              value={saisie?.supplierName ?? ''}
              onChange={(e) => setSaisie((s) => s && { ...s, supplierName: e.target.value })}
              helperText="Le bailleur, la compagnie d’électricité, le transporteur…"
            />
            <TextField
              label="Note"
              multiline
              minRows={2}
              value={saisie?.notes ?? ''}
              onChange={(e) => setSaisie((s) => s && { ...s, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button onClick={() => setSaisie(null)}>Annuler</Button>
          <Button variant="contained" onClick={() => void enregistrer()}>
            {saisie?.id ? 'Enregistrer' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ────────────────────────────────────────────── saisie d'une catégorie */}
      <Dialog open={categorie !== null} onClose={() => setCategorie(null)} fullWidth maxWidth="xs">
        <DialogTitle>{categorie?.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nom"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            value={categorie?.name ?? ''}
            onChange={(e) => setCategorie((c) => c && { ...c, name: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button onClick={() => setCategorie(null)}>Annuler</Button>
          <Button variant="contained" onClick={() => void enregistrerCategorie()}>
            {categorie?.id ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
